import store from '@polleverywhere/redux'
import { slice as activitiesSlice, explorer } from '../../apps/activities/slice'
import listenToSelector from '../../util/listen_to_selector'
import activityFactory from '@polleverywhere/graphql-models/lib/activityFactory'
import { createSlideSet } from '@polleverywhere/slideware-bindings/lib/slideset'
import { preloadPlaceholders } from '@polleverywhere/slideware-bindings/lib/multiple_image_helpers'

/**
 * Load Backbone models for a set of activities objects
 *
 * Also loads nested activities inside an MQA
 *
 * @param {[{type: string, shared_key: string, presenter_key: string?}]} activities
 * @returns [Backbone.Model]
 */
export async function loadActivityModels (activities) {
  const mqaModels = []
  const models = activities.map(async (activity) => {
    const model = await activityFactory({
      type: activity.type,
      shared_key: activity.shared_key,
      presenter_key: activity.presenter_key
    })
    // Ensure we have a presenter_key
    if (!model.has('presenter_key') || model.get('presenter_key') === model.get('shared_key')) {
      await model.fetch({
        data: {
          include: 'poll_instances'
        }
      })
    }
    // If it's an MQA, we also need to preload all of the internal activities
    if (model.type?.() === 'competition' || model.type?.() === 'survey') {
      mqaModels.push(...model.polls.models)
    }
    return model
  })
  return await Promise.all(models.concat(mqaModels))
}

async function preloadActivityPlaceholders (activityModels) {
  const slideSet = await createSlideSet(activityModels)
  const activityPlaceholderUrls = _.uniq(slideSet.getData().map(data => data.placeholderUrl))

  if (activityPlaceholderUrls.length) {
    preloadPlaceholders(activityPlaceholderUrls)
  }
}

function isInTrash () {
  const rootFolder = explorer.selectRootFolder(store.getState())
  return rootFolder.key === 'trash'
}

/**
 * Handle preloading activity placeholders for slideware
 *
 * To speed up slide insertion. We preload on selection (in explorer) or when an activity
 * page is loaded.
 *
 * Note that this is currently can preload the same activity in both cases, as one gives
 * us a plain object and the other a Backbone model. It's not worth optimising away as it
 * likely won't happen often, and this solution is already significantly better than the
 * prior solution of preloading the first 1000 activities a user has on every load.
 */
export default async () => {
  const preloadedActivities = new WeakSet()

  // When explorer activities are selected, preload slideware placeholder images
  listenToSelector((state) => explorer.selectSelectedActivities(state), async (activities) => {
    if (isInTrash()) return

    const activitiesToPreload = activities.filter(activity => {
      if (preloadedActivities.has(activity) || activity.id === -1) {
        // Skip activities we've already preloaded
        // and the default activity
        return false
      } else {
        // Add to list we've preloaded and process this one
        preloadedActivities.add(activity)
        return true
      }
    })
    const activityModels = await loadActivityModels(activitiesToPreload)
    await preloadActivityPlaceholders(activityModels)
  })

  // When a single activity is loaded, preload the placeholder
  listenToSelector((state) => activitiesSlice.selectActivityAsModel(state), async (activityModel) => {
    if (!preloadedActivities.has(activityModel) && activityModel?.id > 0) {
      preloadedActivities.add(activityModel)
      await preloadActivityPlaceholders([activityModel])
    }
  })
}
